import CanvasController from './canvas-controller.js';
import { palette } from '../color.js';
import { getChirpletPhase, getChirpletAngularVelocity } from '../chirp-things.js';

export default class ChirpletEpicycleController extends CanvasController {

    constructor(id, width, height) {
        super(id, width, height);
        
        this.animAmt = 0;
        this.period = 4;
        
        this.centerFreq = 0.05;
        this.chirpRate = 0.0003;
        this.sigma = 40;
        this.amplitude = 80;
        
        this.numSamples = 200;
        this.centerTime = this.numSamples / 2;
        
        this.trailPoints = [];
        this.maxTrailLength = 150;
    }

    setChirpRate(rate) {
        this.chirpRate = rate;
        this.trailPoints = [];
    }

    update(dt, mousePosition) {
        this.animAmt += dt / this.period;
        if (this.animAmt > 1) {
            this.animAmt -= 1;
            this.trailPoints = [];
        }
    }

    render() {
        this.clear();
        this.context.save();
        this.context.translate(this.canvas.width / 2, this.canvas.height / 2);
        
        const currentSample = this.animAmt * this.numSamples;
        
        this.renderCircle(currentSample);
        this.renderTrail();
        this.renderEnvelope(currentSample);
        
        this.context.restore();
    }

    renderCircle(currentSample) {
        const dt = currentSample - this.centerTime;
        const envelope = Math.exp(-(dt * dt) / (2 * this.sigma * this.sigma));
        const phase = getChirpletPhase(currentSample, this.centerTime, this.centerFreq, this.chirpRate);
        
        const currentRadius = this.amplitude * envelope;
        const tipX = currentRadius * Math.cos(phase);
        const tipY = currentRadius * Math.sin(phase);
        
        this.trailPoints.push({ x: tipX, y: tipY, envelope });
        if (this.trailPoints.length > this.maxTrailLength) {
            this.trailPoints.shift();
        }
        
        if (envelope > 0.01) {
            this.context.strokeStyle = palette.cyan;
            this.context.lineWidth = 1;
            this.context.globalAlpha = 0.5 * envelope;
            
            this.context.beginPath();
            this.context.arc(0, 0, currentRadius, 0, 2 * Math.PI);
            this.context.stroke();
            
            this.context.globalAlpha = envelope;
            this.context.strokeStyle = palette.pink;
            this.context.lineWidth = 2;
            this.context.beginPath();
            this.context.moveTo(0, 0);
            this.context.lineTo(tipX, tipY);
            this.context.stroke();
            
            this.context.fillStyle = palette.pink;
            this.context.beginPath();
            this.context.arc(tipX, tipY, 5, 0, 2 * Math.PI);
            this.context.fill();
        }
        
        this.context.globalAlpha = 1;
    }

    renderTrail() {
        if (this.trailPoints.length < 2) return;
        
        for (let i = 1; i < this.trailPoints.length; i++) {
            const prev = this.trailPoints[i - 1];
            const curr = this.trailPoints[i];
            
            const age = 1 - (i / this.trailPoints.length);
            this.context.globalAlpha = Math.max(0.1, curr.envelope * (1 - age * 0.5));
            this.context.strokeStyle = palette.blue;
            this.context.lineWidth = 2;
            
            this.context.beginPath();
            this.context.moveTo(prev.x, prev.y);
            this.context.lineTo(curr.x, curr.y);
            this.context.stroke();
        }
        
        this.context.globalAlpha = 1;
    }

    renderEnvelope(currentSample) {
        const envelopeWidth = 0.8 * this.width;
        const envelopeHeight = 40;
        const envelopeY = this.height / 2 - 30;
        const startX = -envelopeWidth / 2;
        
        this.context.strokeStyle = '#666';
        this.context.lineWidth = 1;
        this.context.beginPath();
        this.context.moveTo(startX, envelopeY);
        this.context.lineTo(startX + envelopeWidth, envelopeY);
        this.context.stroke();
        
        this.context.strokeStyle = palette.cyan;
        this.context.lineWidth = 2;
        this.context.beginPath();
        
        for (let i = 0; i < this.numSamples; i++) {
            const x = startX + (i / this.numSamples) * envelopeWidth;
            const dt = i - this.centerTime;
            const env = Math.exp(-(dt * dt) / (2 * this.sigma * this.sigma));
            const y = envelopeY - env * envelopeHeight;
            
            if (i === 0) {
                this.context.moveTo(x, y);
            } else {
                this.context.lineTo(x, y);
            }
        }
        this.context.stroke();
        
        const markerX = startX + (currentSample / this.numSamples) * envelopeWidth;
        const dt = currentSample - this.centerTime;
        const currentEnv = Math.exp(-(dt * dt) / (2 * this.sigma * this.sigma));
        const markerY = envelopeY - currentEnv * envelopeHeight;
        
        this.context.fillStyle = palette.pink;
        this.context.beginPath();
        this.context.arc(markerX, markerY, 5, 0, 2 * Math.PI);
        this.context.fill();
    }
}
